import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaUsers, FaHandshake, FaAward, FaStore, FaDoorOpen, FaToilet } from 'react-icons/fa';
import logoImage from '../assets/logo.jpg';
import PricingBanner from '../components/PricingBanner';

const AboutContainer = styled.div`
  padding: 0px 0 60px;
  background: var(--background-dark);
`;

const AboutHero = styled.div`
  background: linear-gradient(135deg, var(--background-accent) 0%, var(--background-lighter) 100%);
  padding: 60px 0;
  text-align: center;
  margin-bottom: 80px;
  border-bottom: 5px solid var(--primary-color);
`;

const AboutHeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroLogo = styled.img`
  width: 250px;
  height: 250px;
  object-fit: cover;
  margin: 0 auto 30px;
  display: block;
  border-radius: 50%;
  background: white;
  padding: 12px;
  box-shadow: 0 8px 25px rgba(212, 175, 55, 0.25);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  &:hover {
    transform: scale(1.05) rotate(-2deg);
    box-shadow: 0 12px 35px rgba(212, 175, 55, 0.4);
  }
  
  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    margin-bottom: 20px;
    padding: 10px;
  }
`;

const AboutHeroTitle = styled(motion.h1)`
  font-size: 2.8rem;
  color: var(--text-primary);
  margin-bottom: 20px;
  
  span {
    color: var(--primary-color);
  }
`;

const AboutHeroDescription = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 0;
`;

const AboutSection = styled.section`
  padding: 60px 0;
  background-color: ${props => props.$dark ? 'var(--background-darker)' : 'var(--background-dark)'};
`;

const AboutContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 40px;
  
  @media (max-width: 768px) {
    flex-direction: ${props => props.$reverse ? 'column-reverse' : 'column'};
  }
`;

const AboutInfo = styled.div`
  flex: 1;
`;

const AboutImageContainer = styled.div`
  flex: 1;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const AboutImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: var(--border-radius);
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const AboutTitle = styled(motion.h2)`
  font-size: 2.2rem;
  color: var(--text-primary);
  margin-bottom: 20px;
  
  span {
    color: var(--primary-color);
  }
`;

const AboutDescription = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 25px;
`;

const ValuesSection = styled.section`
  padding: 80px 0;
  background-color: var(--background-dark);
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ValueCard = styled(motion.div)`
  background-color: var(--background-lighter);
  border-radius: var(--border-radius);
  padding: 30px;
  text-align: center;
  transition: var(--transition);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const ValueIcon = styled.div`
  width: 70px;
  height: 70px;
  background-color: rgba(107, 99, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  
  svg {
    font-size: 2rem;
    color: var(--primary-color);
  }
`;

const ValueTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 15px;
`;

const ValueDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
`;

const FacilitiesSection = styled.section`
  padding: 80px 0;
  background-color: var(--background-darker);
`;

const FacilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FacilityCard = styled(motion.div)`
  background-color: var(--background-lighter);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: var(--transition);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const FacilityImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const FacilityContent = styled.div`
  padding: 25px;
`;

const FacilityTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  
  svg {
    color: var(--primary-color);
    margin-right: 10px;
  }
`;

const FacilityDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const CTASection = styled.section`
  padding: 80px 0;
  background: linear-gradient(135deg, rgba(107, 99, 255, 0.9) 0%, rgba(78, 205, 196, 0.9) 100%);
  text-align: center;
`;

const CTAContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CTATitle = styled(motion.h2)`
  font-size: 2.5rem;
  color: white;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTADescription = styled(motion.p)`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(Link)`
  background-color: white;
  color: var(--primary-color);
  padding: 15px 30px;
  border-radius: var(--border-radius);
  font-weight: 600;
  font-size: 1.1rem;
  display: inline-block;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--background-dark);
    color: white;
    transform: translateY(-3px);
  }
`;

const About = () => {
  return (
    <AboutContainer>
      <AboutHero>
        <AboutHeroContent>
          <HeroLogo src={logoImage} alt="Istri Wala Logo" />
          <AboutHeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            About <span>Istriwala</span>
          </AboutHeroTitle>
          <AboutHeroDescription
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            We are Istri Wala - your trusted partner for premium steam ironing and comprehensive laundry solutions. Specializing in professional steam ironing services, we also offer dry cleaning, complete laundry care, and shoe cleaning to meet all your garment care needs with expertise and attention to detail.
          </AboutHeroDescription>
        </AboutHeroContent>
      </AboutHero>

      <AboutSection>
        <AboutContent>
          <AboutInfo>
            <AboutTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Our <span>Story</span>
            </AboutTitle>
            <AboutDescription
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Istri Wala was founded with a clear vision: to provide the finest steam ironing services in the region while offering comprehensive laundry solutions. What started as a small family business focused on perfecting the art of steam ironing has grown into a trusted name, thanks to our unwavering commitment to quality and customer satisfaction.
            </AboutDescription>
            <AboutDescription
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Our core expertise remains in premium steam ironing - using state-of-the-art equipment and techniques to ensure every garment receives perfect finishing. We've expanded to include dry cleaning for delicate fabrics, complete wash & fold laundry services, and professional shoe cleaning. Our modern facility features comfortable waiting lounge, private changing rooms, and complimentary pickup & delivery within 5km for all customers, making professional garment care effortless.
            </AboutDescription>
          </AboutInfo>
          
          <AboutImageContainer>
            <AboutImage 
              src="https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1951&q=80" 
              alt="Our Story"
            />
          </AboutImageContainer>
        </AboutContent>
      </AboutSection>

      <AboutSection $dark>
        <div className="section-title">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Why Choose <span>Istri Wala</span>?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Experience the difference with our premium steam ironing and laundry services
          </motion.p>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px', maxWidth: '1200px', margin: '40px auto 0', padding: '0 20px' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ background: 'white', padding: '30px', borderRadius: 'var(--border-radius)', boxShadow: '0 5px 20px rgba(0,0,0,0.1)', textAlign: 'center' }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🏆</div>
            <h3 style={{ fontSize: '1.4rem', color: 'var(--text-primary)', marginBottom: '10px' }}>Expert Steam Ironing</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Our specialty! Years of experience in professional steam ironing with state-of-the-art equipment ensuring crisp, wrinkle-free results every time.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ background: 'white', padding: '30px', borderRadius: 'var(--border-radius)', boxShadow: '0 5px 20px rgba(0,0,0,0.1)', textAlign: 'center' }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>⏱️</div>
            <h3 style={{ fontSize: '1.4rem', color: 'var(--text-primary)', marginBottom: '10px' }}>Quick Turnaround</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Fast and efficient service with 24-48 hour standard turnaround. Express service available for urgent needs.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ background: 'white', padding: '30px', borderRadius: 'var(--border-radius)', boxShadow: '0 5px 20px rgba(0,0,0,0.1)', textAlign: 'center' }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>💎</div>
            <h3 style={{ fontSize: '1.4rem', color: 'var(--text-primary)', marginBottom: '10px' }}>Premium Quality</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              We use only the finest equipment and techniques. Every garment receives individual attention and care it deserves.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ background: 'white', padding: '30px', borderRadius: 'var(--border-radius)', boxShadow: '0 5px 20px rgba(0,0,0,0.1)', textAlign: 'center' }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🚚</div>
            <h3 style={{ fontSize: '1.4rem', color: 'var(--text-primary)', marginBottom: '10px' }}>Free Pickup & Delivery</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Complimentary pickup and delivery within 5km for all customers. Scheduled at your convenience, no extra charges.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{ background: 'white', padding: '30px', borderRadius: 'var(--border-radius)', boxShadow: '0 5px 20px rgba(0,0,0,0.1)', textAlign: 'center' }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>💰</div>
            <h3 style={{ fontSize: '1.4rem', color: 'var(--text-primary)', marginBottom: '10px' }}>Affordable Pricing</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Competitive membership plans starting from ₹399/month. Save up to 40% with our subscription packages.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            style={{ background: 'white', padding: '30px', borderRadius: 'var(--border-radius)', boxShadow: '0 5px 20px rgba(0,0,0,0.1)', textAlign: 'center' }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🏢</div>
            <h3 style={{ fontSize: '1.4rem', color: 'var(--text-primary)', marginBottom: '10px' }}>Modern Facilities</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Clean, comfortable waiting lounge with refreshments, private changing rooms, and hygienic washroom facilities.
            </p>
          </motion.div>
        </div>
      </AboutSection>

      <ValuesSection>
        <div className="section-title">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Our Values
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            What drives us to provide exceptional service every day
          </motion.p>
        </div>
        
        <ValuesGrid>
          <ValueCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <ValueIcon>
              <FaAward />
            </ValueIcon>
            <ValueTitle>Quality</ValueTitle>
            <ValueDescription>
              We are committed to delivering the highest quality service, paying attention to every detail to ensure your clothes look their best.
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ValueIcon>
              <FaUsers />
            </ValueIcon>
            <ValueTitle>Customer Focus</ValueTitle>
            <ValueDescription>
              Our customers are at the heart of everything we do. We continuously improve our services based on your feedback and needs.
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <ValueIcon>
              <FaHandshake />
            </ValueIcon>
            <ValueTitle>Reliability</ValueTitle>
            <ValueDescription>
              You can count on us to deliver consistent, dependable service every time, with timely pickup and delivery as promised.
            </ValueDescription>
          </ValueCard>
        </ValuesGrid>
      </ValuesSection>

      <FacilitiesSection>
        <div className="section-title">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Our Facilities
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Designed for your comfort and convenience
          </motion.p>
        </div>
        
        <FacilitiesGrid>
          <FacilityCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <FacilityImage 
              src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80" 
              alt="Modern Shop"
            />
            <FacilityContent>
              <FacilityTitle>
                <FaStore /> Modern Shop
              </FacilityTitle>
              <FacilityDescription>
                Our shop is designed with a modern aesthetic, providing a clean and comfortable environment for our customers. Enjoy complimentary refreshments while you wait for your clothes.
              </FacilityDescription>
            </FacilityContent>
          </FacilityCard>
          
          <FacilityCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FacilityImage 
              src="https://images.unsplash.com/photo-1589834390005-5d4fb9bf3d32?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
              alt="Changing Rooms"
            />
            <FacilityContent>
              <FacilityTitle>
                <FaDoorOpen /> Changing Rooms
              </FacilityTitle>
              <FacilityDescription>
                Our private changing rooms allow you to try on freshly pressed clothes before leaving, ensuring everything fits perfectly and meets your expectations.
              </FacilityDescription>
            </FacilityContent>
          </FacilityCard>
          
          <FacilityCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <FacilityImage 
              src="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
              alt="Washroom Facilities"
            />
            <FacilityContent>
              <FacilityTitle>
                <FaToilet /> Washroom Facilities
              </FacilityTitle>
              <FacilityDescription>
                Clean and well-maintained facilities are available for our customers' convenience, making it easy to freshen up while waiting for your perfectly pressed clothes.
              </FacilityDescription>
            </FacilityContent>
          </FacilityCard>
        </FacilitiesGrid>
      </FacilitiesSection>

      <AboutSection $dark>
        <AboutContent $reverse>
          <AboutImageContainer>
            <AboutImage 
              src="https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80" 
              alt="Free Delivery Service"
            />
          </AboutImageContainer>
          
          <AboutInfo>
            <AboutTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Free <span>Pickup & Delivery</span>
            </AboutTitle>
            <AboutDescription
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              We understand that your time is valuable, which is why we offer complimentary pickup and delivery services within a 5km radius of our location for all customers. Our reliable team will collect your items at a time that suits you and return them perfectly pressed.
            </AboutDescription>
            <AboutDescription
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              For customers outside our free delivery zone, we offer affordable delivery options to ensure everyone can benefit from our premium laundry and cleaning services without the hassle of transportation.
            </AboutDescription>
          </AboutInfo>
        </AboutContent>
      </AboutSection>

      <CTASection>
        <CTAContainer>
          <CTATitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Ready to Experience Our Services?
          </CTATitle>
          <CTADescription
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Visit our service center or schedule a pickup today and discover why our customers trust us with all their laundry and cleaning needs.
          </CTADescription>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <CTAButton to="/contact">Contact Us Now</CTAButton>
          </motion.div>
        </CTAContainer>
      </CTASection>
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 40px' }}>
        <PricingBanner
          title="Membership Plans Available!"
          description="Subscribe to our monthly plans and save big! Get daily wear ironing with bonus clothes included."
          discount="Best Value"
          buttonText="View Plans"
          buttonLink="/pricing"
        />
      </div>
    </AboutContainer>
  );
};

export default About;
